'use client';

import React from 'react';
import { ProgressIndicatorComponent } from '../types/content-components.types.js';
import { useComponentAnimation } from './hooks/useComponentAnimation.js';
import { Check, Circle } from 'lucide-react';

interface ProgressStepsProps {
  component: ProgressIndicatorComponent;
}

export const ProgressSteps: React.FC<ProgressStepsProps> = ({ component }) => {
  const { props } = component;
  const { ref, isVisible } = useComponentAnimation(props.animation !== false ? 'fade-in' : 'none');

  const getVariantColors = () => {
    switch (props.variant) {
      case 'primary':
        return { complete: 'bg-learn-sage-500', current: 'bg-learn-sage-400 border-learn-sage-500', future: 'bg-gray-200', line: 'bg-learn-sage-200' };
      case 'success':
        return { complete: 'bg-green-500', current: 'bg-green-400 border-green-500', future: 'bg-gray-200', line: 'bg-green-200' };
      case 'highlight':
        return { complete: 'bg-learn-coral-500', current: 'bg-learn-coral-400 border-learn-coral-500', future: 'bg-gray-200', line: 'bg-learn-coral-200' };
      default:
        return { complete: 'bg-gray-600', current: 'bg-gray-500 border-gray-600', future: 'bg-gray-200', line: 'bg-gray-300' };
    }
  };

  const colors = getVariantColors();
  const animationClass = isVisible ? 'animate-fadeIn' : '';
  
  // Calculate progress percentage
  const currentIndex = props.currentStage !== undefined ? props.currentStage : props.stages.findIndex(s => s.status === 'current');
  const percentage = props.stages.length > 0 ? Math.round(((currentIndex + 1) / props.stages.length) * 100) : 0;

  return (
    <div ref={ref} className={`w-full ${animationClass}`} style={{ opacity: isVisible ? undefined : 0 }}>
      {props.title && (
        <h3 className="text-xl font-bold text-gray-900 mb-4">{props.title}</h3>
      )}

      {/* Progress Percentage */}
      {props.showPercentage && (
        <div className="mb-6">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-gray-700">Overall Progress</span>
            <span className="text-sm font-bold text-gray-900">{percentage}%</span>
          </div>
          <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className={`h-full ${colors.complete} transition-all duration-500`}
              style={{ width: `${percentage}%` }}
            />
          </div>
        </div>
      )}

      {/* Steps */}
      <div className="relative">
        {props.stages.map((stage, index) => {
          const status = stage.status || (index < currentIndex ? 'complete' : index === currentIndex ? 'current' : 'future');
          const isLast = index === props.stages.length - 1;

          return (
            <div key={index} className="relative flex gap-4 pb-8 last:pb-0">
              {/* Status Indicator */}
              <div className="flex flex-col items-center flex-shrink-0">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center shadow-md z-10 transition-all duration-300 ${
                    status === 'complete'
                      ? `${colors.complete} text-white`
                      : status === 'current'
                      ? `border-2 ${colors.current} text-gray-700`
                      : `${colors.future} text-gray-400`
                  }`}
                >
                  {status === 'complete' ? (
                    <Check className="w-5 h-5" />
                  ) : (
                    <span className="text-sm font-bold">{index + 1}</span>
                  )}
                </div>
                
                {/* Connecting Line */}
                {!isLast && (
                  <div className={`w-0.5 ${status === 'complete' ? colors.line : 'bg-gray-200'} flex-1 min-h-[40px] mt-2`} />
                )}
              </div>

              {/* Content */}
              <div className="flex-1 pt-1">
                <h4 className={`text-base font-bold mb-1 ${
                  status === 'complete' ? 'text-gray-900' : status === 'current' ? 'text-gray-900' : 'text-gray-600'
                }`}>
                  {stage.label}
                </h4>
                <p className={`text-sm leading-relaxed ${
                  status === 'complete' ? 'text-gray-700' : status === 'current' ? 'text-gray-700' : 'text-gray-500'
                }`}>
                  {stage.description}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

